/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import useGetUrlParams from "../../../../../hooks/useGetUrlParams";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../constants";
import {usePostRequest} from "../../../../../api/helper";
import * as React from "react";
import {useEffect, useMemo} from "react";
import {toast} from "react-hot-toast";
import {MRT_ColumnDef} from "material-react-table";
import {COLUMN_HEADER_CONTAINED_IN_VEHICLE_PROGRAM_DEVELOPMENT_STAGE} from "../constants";

import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";
import {Checkbox, Typography} from "@mui/material";
import Box from "@mui/material/Box";

interface IReviewAdasSensorsProps {
  developmentStageId: string;
  adasProgramId: string;
  hasSensorMismatch: boolean | null;
  setHasSensorMismatch: React.Dispatch<React.SetStateAction<boolean | null>>;
  selectedSensorPositionIds: Record<string, boolean>;
  setSelectedSensorPositionIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

/**
 * Serializes the sensor position ID based on sensorId and sensorLocation. Used as unique row identifier.
 * @param sensorId
 * @param sensorLocation
 */
const serializeSensorPositionId = (sensorId: string, sensorLocation: string) => {
  return JSON.stringify({sensorId, sensorLocation});
}

const ReviewAdasSensors = (props: IReviewAdasSensorsProps) => {
  const {vehicleProgramId} = useGetUrlParams()
  const {developmentStageId, adasProgramId, selectedSensorPositionIds, setSelectedSensorPositionIds} = props

  const urlReviewAdasSensors = VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.reviewAdasSensors
  const reviewAdasSensorsHandle = usePostRequest(urlReviewAdasSensors)

  const [adasSensorsData, setAdasSensorsData] = React.useState<any>([])

  useEffect(() => {
    setAdasSensorsData([])
  }, [adasProgramId]);


  const [isLoadingReviewAdasSensors, setIsLoadingReviewAdasSensors] = React.useState(false)
  useEffect(() => {
    if (!vehicleProgramId || !developmentStageId || !adasProgramId) return
    setIsLoadingReviewAdasSensors(true)
    reviewAdasSensorsHandle({
      vehicleProgramId,
      developmentStageId,
      adasProgramId,
    }).then((res) => {
      setAdasSensorsData(res)
      const hasSensorMismatch = res.some((item: any) => !item.inDevStage)

      props.setHasSensorMismatch(hasSensorMismatch)
    }).catch((e) => {
      console.error(e)
      toast.error("Failed to link ADAS Program")
    }).finally(() => {
      setIsLoadingReviewAdasSensors(false)
    })
  }, [vehicleProgramId, developmentStageId, adasProgramId]);

  useEffect(() => {
    // default select all sensors that are in dev stage
    const initialSelectedRows: Record<string, boolean> = {};
    adasSensorsData.forEach((sensor: any) => {
        if (sensor.inDevStage) {
          const sensorPositionId = serializeSensorPositionId(sensor.sensorId, sensor.sensorLocation);
          initialSelectedRows[sensorPositionId] = true;
        }
      }
    );
    setSelectedSensorPositionIds(initialSelectedRows);
  }, [adasSensorsData]);

  /**
   * Toggles the selection state of a single row. If it is being unselected, it removes the row from the selectedSensorPositionIds state.
   *
   * @param {string} rowId - The unique ID of the row.
   * @param {boolean} isChecked - Whether the row is selected (true) or deselected (false).
   */
  const handleRowSelection = (rowId: string, isChecked: boolean) => {
    setSelectedSensorPositionIds((prev: Record<string, boolean>) => {
      if (isChecked) {
        // If the row is being selected, add it to the selectedSensorPositionIds state
        return {...prev, [rowId]: true};
      } else {
        // If the row is being unselected, remove it from the selectedSensorPositionIds state
        const {[rowId]: _, ...rest} = prev;
        return rest;
      }
    });
  };

  const handleSelectAllRows = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      // Select all eligible sensors
      const newSelection: Record<string, boolean> = {};
      adasSensorsData.forEach((sensor: any) => {
        if (sensor.inDevStage) {
          const sensorPositionId = serializeSensorPositionId(sensor.sensorId, sensor.sensorLocation);
          newSelection[sensorPositionId] = true;
        }
      });
      setSelectedSensorPositionIds(newSelection);
    } else {
      // Deselect all
      setSelectedSensorPositionIds({});
    }
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: "Select",
        Cell: ({row}) => {
          const disable = !row.original.inDevStage; // Disable checkbox if not in development stage
          const sensorPositionId = serializeSensorPositionId(row.original.sensorId, row.original.sensorLocation);
          return (
            <Checkbox
              disabled={disable}
              checked={selectedSensorPositionIds[sensorPositionId] ?? false}
              onChange={(e) => handleRowSelection(sensorPositionId, e.target.checked)}
            />
          )
        },
        muiTableHeadCellProps: {
          sx: {
            textAlign: "left",
            paddingLeft: "16px"
          },
          children: (() => {
            const eligibleSensorIds = adasSensorsData
              .filter((sensor: any) => sensor.inDevStage)
              .map((sensor: any) => serializeSensorPositionId(sensor.sensorId, sensor.sensorLocation));

            const numSelected = eligibleSensorIds.filter((id: string) => selectedSensorPositionIds[id]).length;
            const allSelected = eligibleSensorIds.length > 0 && numSelected === eligibleSensorIds.length;
            const someSelected = numSelected > 0 && numSelected < eligibleSensorIds.length;
            return (
              <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{width: "100%"}}>
                <Typography variant="body2" fontWeight="bold">Select</Typography>
                <Checkbox
                  style={{paddingLeft: 0}}
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAllRows}
                  inputProps={{'aria-label': 'select all rows'}}
                />
              </Box>
            );
          })()
        }
      },
      {
        accessorKey: 'sensorName',
        id: 'sensorName',
        header: 'Sensor Name',
        size: 300,
      },
      {
        accessorKey: 'sensorType',
        id: 'sensorType',
        header: 'Sensor Type',
      },
      {
        accessorKey: 'sensorLocation',
        id: 'sensorLocation',
        header: 'Sensor Location',
      },
      {
        accessorFn: (originalRow) => originalRow.inDevStage?.toString() ?? '',
        id: 'inDevStage',
        header: COLUMN_HEADER_CONTAINED_IN_VEHICLE_PROGRAM_DEVELOPMENT_STAGE,
        size: 300,
      },
    ], [selectedSensorPositionIds])

  // This is a temporary hack that prevents a ui bug where the table changes height only after a user selectes a checkbox. (Might not always work, setting to low value eg 100 didn't work).
  const [renderTrigger, setRenderTrigger] = React.useState(0);
  useEffect(() => {
  const timeout = setTimeout(() => {
    setRenderTrigger((prev) => prev + 1);
  }, 500);
  return () => clearTimeout(timeout);
}, [selectedSensorPositionIds]);

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <div>
        {props.hasSensorMismatch &&
          <p style={{color: 'red'}}>
            Sensors listed in red are defined in the ADAS program but not in the Vehicle Program development stage, so they cannot be selected.
          </p>
        }
      </div>
      <div style={{flex: 1, overflow: 'auto'}}>
        <ArgosTableSimple
          data={adasSensorsData}
          columns={columns}

          enableBottomToolbar={false}

          muiTableBodyRowProps={({row}) => ({
            //conditionally style selected rows
            sx: {
              backgroundColor: row.original.inDevStage ? 'lightgreen' : '#FF7276',
            },
          })}

          state={{
            isLoading: isLoadingReviewAdasSensors,
          }}

          initialState={{
            density: 'compact'
          }}
        />
      </div>
    </div>
  )
}

export default ReviewAdasSensors